import { useState } from "react";
import { INSPECTION_CAMERA } from "@volca/ui/components/ui/empty-machine";
import { MachineEmptyState } from "@volca/ui/components/ui/machine-empty-state";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  type DayField,
  DocumentListFilters,
  ListTableCard,
  ListTableEmpty,
  useApBusinessTimeZone,
} from "@/accounts-payable/common";
import { type ApDayRange, useGetReceipts } from "@/accounts-payable/query";
import type { ApInvoiceStatus, ApReviewState } from "@/accounts-payable/types";
import { apToday, documentListEmptyMessage } from "@/accounts-payable/utils";
import { useDebounce } from "@/core/hooks";
import {
  dateCol,
  addedCol,
  reviewCol,
  jobCol,
  paymentCol,
  receiptNumberCol,
  statusCol,
  totalCol,
  vendorCol,
} from "./cols";

// Receipts move through the same pipeline states as invoices. Empty means all.
const FILTERS = [
  { key: "all", label: "All", statuses: [] },
  {
    key: "needs_attention",
    label: "Needs attention",
    statuses: ["match_failed", "parse_failed"],
  },
  { key: "needs_job", label: "Needs a job", statuses: ["match_failed"] },
  { key: "unreadable", label: "Couldn't read", statuses: ["parse_failed"] },
  {
    key: "processing",
    label: "Processing",
    statuses: ["pending_parse", "parsing", "parsed", "matching"],
  },
  { key: "matched", label: "Matched", statuses: ["matched"] },
  {
    key: "needs_review",
    label: "Needs review",
    statuses: [],
    reviewStates: ["needs_review"],
  },
  {
    key: "reviewed",
    label: "Reviewed",
    statuses: [],
    reviewStates: ["reviewed"],
  },
] satisfies {
  key: string;
  label: string;
  statuses: ApInvoiceStatus[];
  reviewStates?: ApReviewState[];
}[];

const columns = [
  vendorCol,
  statusCol,
  receiptNumberCol,
  jobCol,
  dateCol,
  addedCol,
  reviewCol,
  paymentCol,
  totalCol,
];

export const ApReceiptsTab = ({
  businessId,
  selectedId = null,
  onSelect,
}: {
  businessId: string | null;
  selectedId?: string | null;
  onSelect: (id: string) => void;
}) => {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dayField, setDayField] = useState<DayField>("added");
  const [dayRange, setDayRange] = useState<ApDayRange | null>(null);
  const debouncedSearch = useDebounce(search, 300);

  const today = apToday(useApBusinessTimeZone());

  const activeFilter = FILTERS.find((f) => f.key === filter);
  const emptyMessage = documentListEmptyMessage("receipt", {
    range: dayRange,
    field: dayField,
    filtered: filter !== "all" || debouncedSearch.trim() !== "",
    today,
  });
  const archiveEmpty = filter === "all" && !debouncedSearch.trim() && !dayRange;

  // Filtering and search run server-side.
  const query = useGetReceipts(businessId, {
    statuses: activeFilter?.statuses,
    reviewStates:
      activeFilter && "reviewStates" in activeFilter
        ? activeFilter.reviewStates
        : undefined,
    search: debouncedSearch,
    added: dayField === "added" ? dayRange : null,
    reviewed: dayField === "reviewed" ? dayRange : null,
  });

  return (
    <Stack align="start" gap={0} className="h-full min-h-0 w-full">
      <DocumentListFilters
        kind="receipt"
        statusFilters={FILTERS}
        status={filter}
        onStatusChange={setFilter}
        dayField={dayField}
        onDayFieldChange={setDayField}
        dayRange={dayRange}
        onDayRangeChange={setDayRange}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Store, receipt #, PO # or job #"
      />

      <ListTableCard
        label="Receipts"
        query={query}
        columns={columns}
        selectedId={selectedId}
        onSelect={onSelect}
        className="mt-0"
        empty={
          archiveEmpty ? (
            <MachineEmptyState
              art={INSPECTION_CAMERA}
              layout="workspace"
              title={emptyMessage.title}
              description={emptyMessage.body}
            />
          ) : (
            <ListTableEmpty {...emptyMessage} />
          )
        }
        error={{
          title: "Couldn’t load receipts",
          body: "Please try again in a moment.",
        }}
      />
    </Stack>
  );
};
